import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Edit, Trash2, Loader2, Users, Store, Eye, EyeOff, ChevronDown, ChevronUp, Link2, Shield, Search, Save } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  manager: "Manager",
  deal_manager: "Deal Manager",
  viewer: "Viewer",
  client: "Client",
  events_manager: "Events Manager",
};

const SECTION_LABELS = {
  deals: "Deals",
  marketplace_reporting: "Marketplace Reporting",
  campaigns: "Campaigns",
  users: "Users",
  logs: "Logs",
  ad_gallery: "Ad Gallery",
  events_rsvps: "Events / RSVPs",
  settings: "Settings",
};

const ACCESS_LEVEL_META = {
  none: { label: "None", color: "#6b7280", bg: "bg-white/5" },
  read: { label: "Read", color: "#f59e0b", bg: "bg-[#f59e0b]/15" },
  edit: { label: "Edit", color: "#22c55e", bg: "bg-[#22c55e]/15" },
};

const getRoleBadgeClass = (role) => ({
  super_admin: "bg-[#ff3737]/20 text-[#ff3737] border-0",
  admin: "bg-orange-500/20 text-orange-400 border-0",
  manager: "bg-yellow-500/20 text-yellow-400 border-0",
  deal_manager: "bg-blue-500/20 text-blue-400 border-0",
  viewer: "bg-gray-500/20 text-gray-400 border-0",
  client: "bg-green-500/20 text-green-400 border-0",
  events_manager: "bg-purple-500/20 text-purple-400 border-0",
}[role] || "bg-gray-500/20 text-gray-400 border-0");

const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-";

const UsersTab = ({ token, requestDelete, canEdit = true }) => {
  const [view, setView] = useState("users"); // "users" | "permissions"
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState("admin");
  const [addingUser, setAddingUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserName, setEditUserName] = useState("");
  const [editUserEmail, setEditUserEmail] = useState("");
  const [editUserCompany, setEditUserCompany] = useState("");
  const [editUserRole, setEditUserRole] = useState("");
  const [editUserPassword, setEditUserPassword] = useState("");
  const [editMarketplaceRevVisible, setEditMarketplaceRevVisible] = useState(false);
  const [editMarketplaceRevPct, setEditMarketplaceRevPct] = useState(100);
  const [savingUser, setSavingUser] = useState(false);
  const [mpSummary, setMpSummary] = useState(null);
  const [mpExpanded, setMpExpanded] = useState(false);

  // Role-permissions matrix state
  const [permData, setPermData] = useState(null); // { sections, access_levels, roles, permissions }
  const [permDirty, setPermDirty] = useState({}); // { role: { section: level } } pending changes
  const [permLoading, setPermLoading] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      if (userRoleFilter && userRoleFilter !== "all") params.append("role_filter", userRoleFilter);
      const response = await axios.get(`${API}/admin/users?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data);
    } catch { toast.error("Failed to fetch users"); }
    finally { setUsersLoading(false); }
  }, [token, userRoleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchPermissions = useCallback(async () => {
    setPermLoading(true);
    try {
      const res = await axios.get(`${API}/admin/role-permissions`, { headers: { Authorization: `Bearer ${token}` } });
      setPermData(res.data);
      setPermDirty({});
    } catch { /* silent — non-blocking for tooltips */ }
    finally { setPermLoading(false); }
  }, [token]);

  // Preload permissions matrix once so role badges can show preview tooltips
  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const fetchMpSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/marketplace-access-summary`, { headers: { Authorization: `Bearer ${token}` } });
      setMpSummary(res.data);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => { fetchMpSummary(); }, [fetchMpSummary]);

  const handleAddUser = async () => {
    if (!newUserName || !newUserEmail || !newUserPassword) { toast.error("Fill all fields"); return; }
    setAddingUser(true);
    try {
      await axios.post(`${API}/admin/users`, { name: newUserName, email: newUserEmail, password: newUserPassword, role: newUserRole }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User created");
      setShowAddUser(false); setNewUserName(""); setNewUserEmail(""); setNewUserPassword(""); setNewUserRole("admin");
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create user"); }
    finally { setAddingUser(false); }
  };

  const fetchPermissionsRefresh = useCallback(async () => {
    setPermLoading(true);
    try {
      const res = await axios.get(`${API}/admin/role-permissions`, { headers: { Authorization: `Bearer ${token}` } });
      setPermData(res.data);
      setPermDirty({});
    } catch { toast.error("Failed to load role permissions"); }
    finally { setPermLoading(false); }
  }, [token]);

  useEffect(() => {
    if (view === "permissions") fetchPermissionsRefresh();
  }, [view, fetchPermissionsRefresh]);

  const setCellLevel = (role, section, level) => {
    if (role === "super_admin") return; // never editable
    setPermDirty((prev) => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [section]: level },
    }));
  };

  const getCellLevel = (role, section) => {
    if (permDirty[role] && permDirty[role][section] !== undefined) return permDirty[role][section];
    return permData?.permissions?.[role]?.[section] || "none";
  };

  const dirtyRoles = useMemo(() => Object.keys(permDirty), [permDirty]);

  const savePermissions = async () => {
    if (dirtyRoles.length === 0) return;
    setSavingPerms(true);
    try {
      // Save each dirty role with the merged matrix (existing + dirty overrides)
      for (const role of dirtyRoles) {
        const merged = { ...(permData.permissions[role] || {}), ...permDirty[role] };
        await axios.put(`${API}/admin/role-permissions/${role}`, { permissions: merged },
          { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(`Saved permissions for ${dirtyRoles.length} role${dirtyRoles.length > 1 ? "s" : ""}`);
      await fetchPermissionsRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save permissions"); }
    finally { setSavingPerms(false); }
  };

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.email, u.company]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [users, searchQuery]);

  const handleEditUser = (user) => {
    setEditingUser(user); setEditUserName(user.name); setEditUserEmail(user.email);
    setEditUserCompany(user.company || ""); setEditUserRole(user.role); setEditUserPassword("");
    const mp = user.marketplace_permissions || {};
    setEditMarketplaceRevVisible(mp.revenue_visible || false);
    setEditMarketplaceRevPct(mp.revenue_percentage ?? 100);
    setShowEditUser(true);
  };

  const handleSaveEditUser = async () => {
    if (!editingUser) return;
    setSavingUser(true);
    try {
      const data = { name: editUserName, email: editUserEmail, company: editUserCompany || null, role: editUserRole };
      if (editUserPassword) data.password = editUserPassword;
      // Marketplace permissions (for non-super-admin users)
      if (editUserRole !== "super_admin") {
        data.marketplace_permissions = {
          revenue_visible: editMarketplaceRevVisible,
          revenue_percentage: parseFloat(editMarketplaceRevPct) || 100,
        };
      }
      await axios.put(`${API}/admin/users/${editingUser.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("User updated");
      setShowEditUser(false);
      fetchUsers();
      fetchMpSummary();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update user"); }
    finally { setSavingUser(false); }
  };

  const handleDeleteUser = (userId, role, name) => {
    if (role === "super_admin") { toast.error("Cannot delete super admin accounts"); return; }
    requestDelete("user", userId, name, async () => {
      try {
        await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(prev => prev.filter(u => u.id !== userId));
        toast.success("User deleted");
      } catch { toast.error("Failed to delete user"); }
    });
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white font-mono">User Management</h1>
          <p className="text-[#ebebeb]/60 mt-1 font-mono text-sm">
            {view === "users" ? "Manage all users: admins and clients" : "Configure access levels per role across the platform"}
          </p>
        </div>
        {view === "users" && canEdit && (
          <button onClick={() => setShowAddUser(true)} className="ingenium-button" data-testid="add-user-btn">
            <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add User</span>
          </button>
        )}
        {view === "permissions" && canEdit && dirtyRoles.length > 0 && (
          <button
            onClick={savePermissions}
            disabled={savingPerms}
            className="ingenium-button"
            data-testid="save-permissions-btn"
          >
            <span className="flex items-center gap-2">
              {savingPerms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save changes ({dirtyRoles.length})
            </span>
          </button>
        )}
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setView("users")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full font-mono text-sm transition-colors ${view === "users" ? 'bg-[#ff3737] text-white' : 'border border-white/10 text-[#ebebeb]/60 hover:bg-white/5'}`}
          data-testid="user-view-users"
        >
          <Users className="w-3.5 h-3.5" /> Users <span className="text-xs opacity-60">({users.length})</span>
        </button>
        <button
          onClick={() => setView("permissions")}
          className={`flex items-center gap-2 px-4 py-2 rounded-full font-mono text-sm transition-colors ${view === "permissions" ? 'bg-[#ff3737] text-white' : 'border border-white/10 text-[#ebebeb]/60 hover:bg-white/5'}`}
          data-testid="user-view-permissions"
        >
          <Shield className="w-3.5 h-3.5" /> Role Permissions
        </button>
      </div>

      {view === "users" && (
        <>
      <Card className="feature-card mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[#ebebeb]/60 font-mono text-sm">Filter by role:</span>
              <div className="flex gap-2 flex-wrap">
                {[{ value: "all", label: "All Users" }, { value: "admin", label: "Admin Users" }, { value: "client", label: "Clients" }].map((filter) => (
                  <button key={filter.value} onClick={() => setUserRoleFilter(filter.value)}
                    className={`px-4 py-2 rounded-full font-mono text-sm transition-colors ${(userRoleFilter === filter.value || (!userRoleFilter && filter.value === "all")) ? 'bg-[#ff3737] text-white' : 'border border-white/20 text-[#ebebeb]/60 hover:bg-white/5'}`}
                    data-testid={`filter-${filter.value}-btn`}>{filter.label}</button>
                ))}
              </div>
            </div>
            <div className="relative w-full lg:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ebebeb]/40" />
              <Input
                placeholder="Search by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="form-input pl-10 w-full"
                data-testid="user-search-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Marketplace Access Summary */}
      {mpSummary && mpSummary.total_eligible > 0 && (
        <Card className="bg-[#1e2124] border-[#2a2d31] mb-6" data-testid="marketplace-access-summary">
          <CardContent className="p-0">
            <button
              onClick={() => setMpExpanded(!mpExpanded)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-white/[0.02] transition-colors"
              data-testid="mp-summary-toggle"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#ff3737]/10 flex items-center justify-center">
                  <Store className="w-4 h-4 text-[#ff3737]" />
                </div>
                <div>
                  <p className="text-white text-sm font-mono font-medium">Marketplace Access</p>
                  <div className="flex items-center gap-4 mt-0.5">
                    <span className="text-[#ebebeb]/40 text-xs font-mono">{mpSummary.total_eligible} eligible users</span>
                    <span className="text-xs font-mono flex items-center gap-1">
                      <Eye className="w-3 h-3 text-[#22c55e]" />
                      <span className="text-[#22c55e]">{mpSummary.with_revenue_access}</span>
                      <span className="text-[#ebebeb]/30">revenue</span>
                    </span>
                    <span className="text-xs font-mono flex items-center gap-1">
                      <Link2 className="w-3 h-3 text-[#3b82f6]" />
                      <span className="text-[#3b82f6]">{mpSummary.with_linked_deals}</span>
                      <span className="text-[#ebebeb]/30">with deals</span>
                    </span>
                  </div>
                </div>
              </div>
              {mpExpanded ? <ChevronUp className="w-4 h-4 text-[#ebebeb]/40" /> : <ChevronDown className="w-4 h-4 text-[#ebebeb]/40" />}
            </button>

            {mpExpanded && (
              <div className="border-t border-[#2a2d31]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#2a2d31] hover:bg-transparent">
                      <TableHead className="text-[#ebebeb]/50 font-mono text-xs">User</TableHead>
                      <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Role</TableHead>
                      <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Deals</TableHead>
                      <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Revenue Access</TableHead>
                      <TableHead className="text-[#ebebeb]/50 font-mono text-xs">Rev %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mpSummary.users.map((u) => (
                      <TableRow key={u.id} className="border-[#2a2d31] hover:bg-white/[0.03]" data-testid={`mp-user-${u.id}`}>
                        <TableCell>
                          <div>
                            <p className="text-white text-xs font-mono">{u.name}</p>
                            <p className="text-[#ebebeb]/40 text-[10px] font-mono">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getRoleBadgeClass(u.role)} text-[10px]`}>{ROLE_LABELS[u.role]}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-mono font-medium ${u.deal_count > 0 ? "text-[#3b82f6]" : "text-[#ebebeb]/30"}`}>
                            {u.deal_count > 0 ? `${u.deal_count} deal${u.deal_count > 1 ? "s" : ""}` : "None"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {u.revenue_visible ? (
                            <span className="inline-flex items-center gap-1 text-[#22c55e] text-xs font-mono">
                              <Eye className="w-3 h-3" /> Visible
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[#ebebeb]/30 text-xs font-mono">
                              <EyeOff className="w-3 h-3" /> Hidden
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-mono ${u.revenue_visible ? "text-[#f59e0b]" : "text-[#ebebeb]/20"}`}>
                            {u.revenue_visible ? `${u.revenue_percentage}%` : "-"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="feature-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="data-table">            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-[#ebebeb]/60 font-mono">Name</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Email</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Company</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Role</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Created</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#ff3737] mx-auto" /></TableCell></TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12"><Users className="w-12 h-12 text-[#ebebeb]/30 mx-auto mb-3" /><p className="text-[#ebebeb]/60 font-mono">{searchQuery ? "No users match your search" : "No users found"}</p></TableCell></TableRow>
              ) : filteredUsers.map((user) => (
                <TableRow key={user.id} className="border-white/5" data-testid={`user-row-${user.id}`}>
                  <TableCell className="text-white font-mono">{user.name}</TableCell>
                  <TableCell className="text-[#ebebeb]/70 font-mono">{user.email}</TableCell>
                  <TableCell className="text-[#ebebeb]/70 font-mono">{user.company || '-'}</TableCell>
                  <TableCell>
                    <div className="relative inline-block group">
                      <Badge className={`${getRoleBadgeClass(user.role)} cursor-help`} data-testid={`role-badge-${user.id}`}>
                        {ROLE_LABELS[user.role] || user.role}
                      </Badge>
                      {permData?.permissions?.[user.role] && (
                        <div
                          className="absolute left-0 top-full mt-2 z-30 hidden group-hover:block w-64 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl p-3"
                          data-testid={`role-tooltip-${user.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${getRoleBadgeClass(user.role)}`}>
                              {ROLE_LABELS[user.role] || user.role}
                            </span>
                            <span className="text-[#ebebeb]/40 text-[10px] font-mono">access map</span>
                          </div>
                          <div className="space-y-1">
                            {permData.sections.map((s) => {
                              const lvl = permData.permissions[user.role]?.[s] || "none";
                              const meta = ACCESS_LEVEL_META[lvl];
                              return (
                                <div key={s} className="flex items-center justify-between text-[11px] font-mono">
                                  <span className="text-[#ebebeb]/70">{SECTION_LABELS[s] || s}</span>
                                  <span style={{ color: meta.color }} className="font-medium">
                                    {meta.label}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[#ebebeb]/60 text-sm font-mono">{formatDate(user.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <div className="flex items-center justify-end gap-1">
                        <button className="p-2 text-[#ebebeb]/60 hover:text-white transition-colors" onClick={() => handleEditUser(user)} data-testid={`edit-user-${user.id}`}><Edit className="w-4 h-4" /></button>
                        <button className="p-2 text-red-400 hover:text-red-300 transition-colors" onClick={() => handleDeleteUser(user.id, user.role, user.name)} data-testid={`delete-user-${user.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <span className="text-[#ebebeb]/30 text-xs font-mono">Read-only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
        </>
      )}

      {/* Role Permissions Matrix View */}
      {view === "permissions" && (
        <Card className="feature-card overflow-hidden" data-testid="permissions-matrix-card">
          <div className="overflow-x-auto">
            {permLoading || !permData ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-[#ff3737]" />
              </div>
            ) : (
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-[#ebebeb]/60 font-mono sticky left-0 bg-[#1e2124] z-10 min-w-[180px]">Section</TableHead>
                    {permData.roles.map((role) => (
                      <TableHead key={role} className="text-[#ebebeb]/60 font-mono text-center min-w-[140px]">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] ${getRoleBadgeClass(role)}`}>
                          {ROLE_LABELS[role]}
                        </span>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permData.sections.map((section) => (
                    <TableRow key={section} className="border-white/5" data-testid={`perm-row-${section}`}>
                      <TableCell className="text-white font-mono sticky left-0 bg-[#1e2124] z-10">
                        {SECTION_LABELS[section] || section}
                      </TableCell>
                      {permData.roles.map((role) => {
                        const level = getCellLevel(role, section);
                        const isLocked = role === "super_admin" || !canEdit;
                        const isDirty = permDirty[role]?.[section] !== undefined;
                        return (
                          <TableCell key={role} className="text-center">
                            <div className="inline-flex rounded-md overflow-hidden border border-white/10" data-testid={`perm-cell-${role}-${section}`}>
                              {permData.access_levels.map((lvl) => {
                                const meta = ACCESS_LEVEL_META[lvl];
                                const active = level === lvl;
                                return (
                                  <button
                                    key={lvl}
                                    onClick={() => setCellLevel(role, section, lvl)}
                                    disabled={isLocked}
                                    className={`px-2 py-1 text-[10px] font-mono transition-colors ${
                                      active ? meta.bg : "bg-transparent hover:bg-white/5"
                                    } ${isLocked ? "cursor-not-allowed opacity-60" : ""} ${
                                      isDirty && active ? "ring-1 ring-[#ff3737]/40" : ""
                                    }`}
                                    style={{ color: active ? meta.color : "rgba(235,235,235,0.4)" }}
                                    data-testid={`perm-btn-${role}-${section}-${lvl}`}
                                  >
                                    {meta.label}
                                  </button>
                                );
                              })}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="px-4 py-3 border-t border-white/5 text-[11px] font-mono text-[#ebebeb]/40">
            Super Admin always has full edit access and cannot be modified. Changes are staged client-side until you click <span className="text-[#ebebeb]/70">Save changes</span>.
          </div>
        </Card>
      )}

      {/* Add User Modal */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="bg-[#191b1d] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-mono">Add User</DialogTitle><DialogDescription className="text-[#ebebeb]/50">Create a new user account</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <Input placeholder="Full Name" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} className="form-input" data-testid="new-user-name" />
            <Input placeholder="Email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} className="form-input" data-testid="new-user-email" />
            <Input type="password" placeholder="Password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} className="form-input" data-testid="new-user-password" />
            <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} className="form-input w-full" data-testid="new-user-role">
              {Object.entries(ROLE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setShowAddUser(false)} className="px-4 py-2 rounded-lg border border-white/10 text-[#ebebeb]/60">Cancel</button>
            <button onClick={handleAddUser} disabled={addingUser} className="ingenium-button" data-testid="submit-new-user">
              {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create User"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="bg-[#191b1d] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-mono">Edit User</DialogTitle><DialogDescription className="text-[#ebebeb]/50">Update user details</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <Input placeholder="Full Name" value={editUserName} onChange={(e) => setEditUserName(e.target.value)} className="form-input" />
            <Input placeholder="Email" value={editUserEmail} onChange={(e) => setEditUserEmail(e.target.value)} className="form-input" />
            <Input placeholder="Company" value={editUserCompany} onChange={(e) => setEditUserCompany(e.target.value)} className="form-input" />
            <select value={editUserRole} onChange={(e) => setEditUserRole(e.target.value)} className="form-input w-full">
              {Object.entries(ROLE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            <Input type="password" placeholder="New Password (leave blank to keep)" value={editUserPassword} onChange={(e) => setEditUserPassword(e.target.value)} className="form-input" />
            {/* Marketplace Permissions */}
            {editUserRole && editUserRole !== "super_admin" && editUserRole !== "events_manager" && (
              <div className="border border-white/10 rounded-lg p-4 space-y-3" data-testid="marketplace-permissions-section">
                <p className="text-[#ebebeb]/70 text-sm font-mono font-medium">Marketplace Permissions</p>
                <p className="text-[#ebebeb]/40 text-xs font-mono">Controls what this user sees in Marketplace reports. Data is filtered by their associated deals.</p>
                <div className="flex items-center justify-between">
                  <label className="text-[#ebebeb]/60 text-sm font-mono">Show Revenue & Margin</label>
                  <button
                    type="button"
                    onClick={() => setEditMarketplaceRevVisible(!editMarketplaceRevVisible)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editMarketplaceRevVisible ? "bg-[#ff3737]" : "bg-white/20"}`}
                    data-testid="toggle-revenue-visible"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editMarketplaceRevVisible ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </div>
                {editMarketplaceRevVisible && (
                  <div>
                    <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Revenue % shown (e.g. 60 = user sees 60% of actual revenue)</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={editMarketplaceRevPct}
                      onChange={(e) => setEditMarketplaceRevPct(e.target.value)}
                      className="form-input w-32"
                      data-testid="revenue-percentage-input"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setShowEditUser(false)} className="px-4 py-2 rounded-lg border border-white/10 text-[#ebebeb]/60">Cancel</button>
            <button onClick={handleSaveEditUser} disabled={savingUser} className="ingenium-button">
              {savingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsersTab;
